import { useState, useEffect, useContext } from 'react';
import {
    Box,
    Typography,
    Tooltip,
    Chip,
    IconButton,
    Link,
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import {
    CheckCircleOutline,
    NavigateBefore as NavigateBeforeIcon,
    NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../../lib/dateLocale';
import { useCrud, useInteractionsFiles, useToggle } from '../../../lib/hooks';
import {
    AppContext,
    SttTable,
    SttTopControls,
    SttDialog,
} from '../../../sporttia/all';
import { formatPriceByLocale, fullName } from '../../../lib/utils';
import ModalParticipant from './ModalParticipant';
import { useParticipantFuncs } from './ParticipantFunctions';
import colors from '../../../styles/Colors';
import PaymentModal from '../../../components/paymentModal/PaymentModal';
import { PERMISSIONS } from '../../../lib/permissions';

const rowsPerPage = 20;

// These types must match ModalParticipant.tsx for compatibility
type FormAnswer = {
    value: unknown;
};

type FormField = {
    id: string | number;
    name?: string;
    type?: string;
    answer?: FormAnswer;
};

type User = {
    id?: string | number;
    name?: string;
    surname1?: string;
    surname2?: string;
    displayName?: string;
};

type Payment = {
    id: string | number;
    paymentDate?: string;
    paymentForm?: string;
    paymentFormLang?: string;
    price?: number;
    status?: string;
};

// This type is for display and ModalParticipant compatibility
type Participant = {
    id?: string | number;
    name?: string;
    surname1?: string;
    surname2?: string;
    displayName?: string;
    user?: User;
    creator?: User;
    form?: FormField[];
    enrollment?: Payment;
    mobile?: string;
    email?: string;
    notes?: string;
    team?: { id: string | number; name?: string };
    admin?: boolean;
    paymentForm?: string;
};

type Team = {
    id: string | number;
    name: string;
    participants?: Participant[];
};

type Category = {
    id: string | number;
    name: string;
};

type Event = {
    id: string | number;
    name: string;
    teamable: boolean;
    maxPlayers?: number | string;
    form?: FormField[];
    categories?: Category[];
};

type TeamsData = {
    count: number;
    rows: Team[];
};

type Props = {
    event: Event;
};

export default function EventTeams({ event }: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { downloadFile } = useInteractionsFiles();
    const [teams, setTeams, , Get, , , Delete] = useCrud<TeamsData>({
        count: 0,
        rows: [],
    });
    const [saveParticipant, deleteParticipant, demoteAdmin] =
        useParticipantFuncs();
    const [participant, setParticipant] = useState<Participant | undefined>();
    const [category, setCategory] = useState<Category | undefined>(
        event.categories && event.categories[0],
    );
    const [page, setPage] = useState<number>(1);
    const [filters, setFilters] = useState<{ name: string }>({
        name: '',
    });
    const [trash, toggleTrash] = useToggle(() => {
        loadTeams();
    });
    const [idPayModal, setIdPayModal] = useState<number | null>(null);
    const [teamToDelete, setTeamToDelete] = useState<Team | null>(null);

    function loadTeams(
        params: { page?: number; rows?: number } = { page, rows: rowsPerPage },
    ): void {
        Get(`/events/categories/${category?.id}/teams`, {
            ...params,
            trash,
        }).then((result: unknown) => {
            const teamsData = result as TeamsData;
            if (teamsData.rows) {
                setTeams(teamsData);
            }
        });
    }

    useEffect(() => {
        if (category?.id) {
            loadTeams({ page, rows: rowsPerPage });
        }
    }, [page, category]);

    function download(mode: 'pdf' | 'csv'): void {
        const type = mode === 'pdf' ? 'application/pdf' : 'text/plain';
        downloadFile(
            `/events/categories/${category?.id}/participants.${mode}`,
            type,
            { page, ...filters, trash },
            `${event.name}_${category?.name}_${t('Teams')}.${mode}`,
        );
    }

    function deleteTeam(team: Team): void {
        Delete(`/events/teams/${team.id}`).then(() => {
            const filteredTeams = teams.rows.filter(
                (t: any) => t.id !== team.id,
            );
            setTeams({
                count: filteredTeams.length,
                rows: filteredTeams,
            });
        });
    }

    function findTargetTeam(targetTeamId: unknown): Team | undefined {
        if (targetTeamId === undefined || targetTeamId === null) {
            return undefined;
        }
        if (String(targetTeamId) === '0') {
            return undefined;
        }
        return teams.rows.find(
            (tm: Team) => String(tm.id) === String(targetTeamId),
        );
    }

    function exceedsTeamCapacity(
        targetTeam: Team,
        participantToSave: Participant,
    ): boolean {
        const maxPlayers = Number(event.maxPlayers);
        if (!(maxPlayers > 0)) return false;

        const alreadyInTargetTeam =
            participantToSave.id !== undefined &&
            targetTeam.participants?.some(
                (p: Participant) =>
                    p.id !== undefined &&
                    String(p.id) === String(participantToSave.id),
            );
        if (alreadyInTargetTeam) return false;

        const currentCount = targetTeam.participants?.length ?? 0;
        return currentCount >= maxPlayers;
    }

    function getPreviousAdmins(
        targetTeam: Team,
        participantToSave: Participant,
    ): Participant[] {
        return (targetTeam.participants ?? []).filter(
            (p: Participant) =>
                !!p.admin &&
                p.id !== undefined &&
                p.id !== null &&
                String(p.id) !== String(participantToSave.id),
        );
    }

    async function handleSaveParticipant(input: any): Promise<void> {
        if (!category) return;

        const participantToSave = input as Participant;
        const targetTeam = findTargetTeam(participantToSave.team?.id);

        if (targetTeam && exceedsTeamCapacity(targetTeam, participantToSave)) {
            cxt.showMessage('E', t('event.teamFull'));
            return;
        }

        try {
            await saveParticipant(participantToSave, category.id);

            if (participantToSave.admin && targetTeam) {
                const previousAdmins = getPreviousAdmins(
                    targetTeam,
                    participantToSave,
                );
                if (previousAdmins.length > 0) {
                    const results = await Promise.allSettled(
                        previousAdmins.map((admin: Participant) =>
                            demoteAdmin({
                                ...admin,
                                team: {
                                    id: targetTeam.id,
                                    name: targetTeam.name,
                                },
                            }),
                        ),
                    );
                    const failed = results.filter(
                        (r) => r.status === 'rejected',
                    );
                    if (failed.length > 0) {
                        console.error(
                            'Failed to demote previous admin(s):',
                            failed,
                        );
                    }
                }
            }

            setParticipant(undefined);
            loadTeams();
        } catch (error) {
            console.error('Failed to save participant:', error);
        }
    }

    function Pagination({
        page: receivedPage,
        perPage,
        total,
    }: {
        page: number;
        perPage: number;
        total: number;
    }): React.ReactElement {
        return (
            <Box>
                {(receivedPage - 1) * perPage + 1} -{' '}
                {receivedPage * perPage > total
                    ? total
                    : receivedPage * perPage}{' '}
                {t('of')} {total}
                <IconButton
                    disabled={receivedPage < 2}
                    onClick={() => {
                        setPage(receivedPage - 1);
                    }}
                >
                    <NavigateBeforeIcon />
                </IconButton>
                <IconButton
                    disabled={receivedPage < Math.floor(total / perPage)}
                    onClick={() => {
                        setPage(receivedPage + 1);
                    }}
                >
                    <NavigateNextIcon />
                </IconButton>
            </Box>
        );
    }

    return (
        <>
            <SttTopControls
                pl={0}
                fields={[
                    {
                        caption: t('Category'),
                        name: 'category',
                        type: 'select' as const,
                        value: category?.id ? String(category.id) : '',
                        mandatory: false,
                        cleanable: false,
                        xs: 12,
                        sm: 12,
                        md: 8,
                        lg: 8,
                        options: event.categories
                            ? event.categories.map((cat: any) => ({
                                  label: cat.name,
                                  value: cat.id,
                              }))
                            : [],
                        onChange: ({
                            value,
                        }: {
                            name: string | undefined;
                            value: unknown;
                        }) => {
                            setCategory(
                                event.categories &&
                                    event.categories.find(
                                        (cat) => cat.id === value,
                                    ),
                            );
                        },
                    },
                ]}
                mainAction={{
                    type: 'create',
                    permission: PERMISSIONS.EVENT.MODIFY,
                    onClick: () => {
                        setParticipant({
                            user: {
                                id: 0,
                                name: '',
                            },
                            team: {
                                id: '0',
                            },
                            mobile: '',
                            email: '',
                            notes: '',
                            form: event.form ? event.form : [],
                            admin: false,
                        });
                    },
                }}
                menu={[
                    {
                        caption: `${t('generic.download')} PDF`,
                        onClick: () => {
                            download('pdf');
                        },
                    },
                    {
                        caption: `${t('generic.download')} CSV`,
                        onClick: () => {
                            download('csv');
                        },
                    },
                ]}
                onChange={({
                    name,
                    value,
                }: {
                    name: string | undefined;
                    value: unknown;
                }) => {
                    if (name) {
                        setFilters({ ...filters, [name]: value as string });
                    }
                }}
                onFilter={loadTeams}
                onToggleTrash={toggleTrash}
            />
            <Box display="flex" justifyContent="flex-end" mt={3}>
                {teams && (
                    <Pagination
                        page={page}
                        perPage={rowsPerPage}
                        total={teams.count}
                    />
                )}
            </Box>
            <Box>
                {teams.rows.map((team: any) => (
                    <Box mb={3} key={team.id}>
                        <Box mb={1} display="flex" alignItems="center">
                            <Typography>{team.name}</Typography>
                            <Link
                                component="button"
                                className="text-brand-text ml-4 cursor-pointer no-underline hover:underline"
                                onClick={(e: React.MouseEvent) => {
                                    e.preventDefault();
                                    setTeamToDelete(team);
                                }}
                            >
                                [{t('Delete')}]
                            </Link>
                        </Box>
                        {team.participants && (
                            <SttTable
                                autoload={false}
                                hidePagination
                                data={{
                                    count: team.participants.length,
                                    rows: team.participants,
                                }}
                                columns={[
                                    {
                                        title: t('Name'),
                                        align: 'left',
                                        type: 'link',
                                        value: (row: Participant) =>
                                            fullName({
                                                ...row.user,
                                                name: row.name,
                                                surname1: row.surname1,
                                                surname2: row.surname2,
                                                displayName: row.displayName,
                                            }),
                                        onClick: (row: Participant) => {
                                            setParticipant(row);
                                        },
                                        style: { width: 330 },
                                    },
                                    {
                                        title: t('RegisteredBy'),
                                        type: 'text',
                                        align: 'left',
                                        value: (row: Participant) =>
                                            fullName(row.creator),
                                        style: { width: 120 },
                                    },
                                    {
                                        title: t('Admin'),
                                        type: 'active',
                                        field: 'admin',
                                        style: { width: 100 },
                                    },
                                    {
                                        title: t('Form'),
                                        value: (row: Participant) =>
                                            row.form?.map(
                                                (item: any) =>
                                                    item.answer?.value && (
                                                        <Tooltip
                                                            arrow
                                                            key={item.id}
                                                            placement="top"
                                                            title={
                                                                item.name || ''
                                                            }
                                                            sx={{
                                                                marginRight: 0.5,
                                                            }}
                                                        >
                                                            <Chip
                                                                size="small"
                                                                label={(() => {
                                                                    let label: React.ReactNode;

                                                                    if (
                                                                        item.type ===
                                                                        'DATE'
                                                                    ) {
                                                                        label =
                                                                            format(
                                                                                parseISO(
                                                                                    item
                                                                                        .answer
                                                                                        .value as string,
                                                                                ),
                                                                                'dd-MM-yyyy',
                                                                                {
                                                                                    locale: getDateLocale(),
                                                                                },
                                                                            );
                                                                    } else if (
                                                                        item.type ===
                                                                        'CHECK'
                                                                    ) {
                                                                        label =
                                                                            (
                                                                                <CheckCircleOutline
                                                                                    style={{
                                                                                        position:
                                                                                            'relative',
                                                                                        top: 2,
                                                                                        color: colors.green,
                                                                                    }}
                                                                                />
                                                                            );
                                                                    } else {
                                                                        label =
                                                                            item
                                                                                .answer
                                                                                .value as React.ReactNode;
                                                                    }

                                                                    return label;
                                                                })()}
                                                            />
                                                        </Tooltip>
                                                    ),
                                            ),
                                        style: { width: 300 },
                                    },
                                    {
                                        title: t('Inscription'),
                                        value: (row: Participant) =>
                                            row.enrollment && (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent:
                                                            'center',
                                                    }}
                                                >
                                                    <div style={{ flex: 1 }}>
                                                        {row.enrollment
                                                            .paymentDate &&
                                                            format(
                                                                parseISO(
                                                                    row
                                                                        .enrollment
                                                                        .paymentDate,
                                                                ),
                                                                'd MMM yyyy',
                                                                {
                                                                    locale: getDateLocale(),
                                                                },
                                                            )}
                                                    </div>
                                                    <Box mx={2}>
                                                        {
                                                            row.enrollment
                                                                .paymentFormLang
                                                        }
                                                    </Box>
                                                    <Link
                                                        onClick={() => {
                                                            setIdPayModal(
                                                                Number(
                                                                    row
                                                                        .enrollment!
                                                                        .id,
                                                                ),
                                                            );
                                                        }}
                                                        style={{
                                                            flex: 1,
                                                            color:
                                                                row.enrollment
                                                                    .status ===
                                                                'PAID'
                                                                    ? colors.green
                                                                    : colors.red,
                                                        }}
                                                    >
                                                        {formatPriceByLocale(
                                                            row.enrollment
                                                                .price,
                                                            cxt?.sc?.city
                                                                ?.country
                                                                ?.currency,
                                                        )}
                                                    </Link>
                                                </div>
                                            ),
                                    },
                                ]}
                            />
                        )}
                    </Box>
                ))}
            </Box>

            {event && category && participant && (
                <ModalParticipant
                    item={participant as any}
                    event={event}
                    category={category}
                    teams={teams.rows}
                    onSave={handleSaveParticipant}
                    onClose={() => {
                        setParticipant(undefined);
                    }}
                    onDelete={(participantToDelete: any) => {
                        deleteParticipant(participantToDelete)
                            .then(() => {
                                setParticipant(undefined);
                                loadTeams();
                            })
                            .catch(() => {});
                    }}
                />
            )}

            {idPayModal && (
                <PaymentModal
                    idPayment={idPayModal}
                    onPay={() => loadTeams()}
                    onDeleted={() => loadTeams()}
                    onRecovered={() => loadTeams()}
                    onRefund={() => loadTeams()}
                    onClose={() => {
                        setIdPayModal(null);
                    }}
                />
            )}

            <SttDialog
                open={Boolean(teamToDelete)}
                title={t('event.deleteTeam')}
                content={teamToDelete?.name}
                onClose={() => setTeamToDelete(null)}
                buttons={[
                    {
                        type: 'cancel',
                        onClick: () => setTeamToDelete(null),
                    },
                    {
                        type: 'delete',
                        permission: PERMISSIONS.EVENT.MODIFY,
                        onClick: () => {
                            if (teamToDelete) {
                                deleteTeam(teamToDelete);
                                setTeamToDelete(null);
                            }
                        },
                    },
                ]}
            />
        </>
    );
}
